/**
 * The Data Room root: the six permanent top-level folders, always visible —
 * empty or not — plus the recently updated strip, a title search across the
 * whole room, and the two global actions, create and download.
 *
 * The six cards are vocabulary, not data: they render from the domain's
 * category list, so an empty Data Room still shows its whole shape. Counts
 * come from the one tree read the folder pages share.
 *
 * The page is gated the way `/team` is: the server-computed capability
 * mirror hides it as a usability choice, and the server's own refusal —
 * the standard not-available 404 — is what actually protects it.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { DATA_ROOM_CATEGORIES } from '@mouja/domain/data-room';

import { DATA_ROOM_EXPORT_URL, fetchDataRoom } from '../api.js';
import { EventTime, useWorkspaceCopy } from '../components.js';
import { useSession } from '../session.js';
import { workspaceSlots } from '../slots.js';
import { TextField } from '../ui.js';
import {
  FailurePanel,
  LoadingPanel,
  PageTitle,
  SectionHeading,
  useAsync,
} from './common.js';
import { NotAvailablePage } from './NotAvailablePage.js';

export function DataRoomPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const [filter, setFilter] = useState('');

  const load = useCallback(() => fetchDataRoom(), []);
  const { state, reload } = useAsync(load);

  const documents = state.status === 'ready' ? state.value.documents : [];
  const deletedCount =
    state.status === 'ready' ? state.value.deletedDocuments.length : 0;

  const matches = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle.length === 0) return null;
    return documents.filter((document) =>
      document.title.toLowerCase().includes(needle),
    );
  }, [documents, filter]);

  const recent = useMemo(
    () =>
      [...documents].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 5),
    [documents],
  );

  const countByCategory = useMemo(() => {
    const counts = new Map<string, number>();
    for (const document of documents) {
      counts.set(document.category, (counts.get(document.category) ?? 0) + 1);
    }
    return counts;
  }, [documents]);

  if (session.status === 'authenticated' && !session.profile.mayUseDataRoom) {
    return <NotAvailablePage />;
  }
  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed') {
    if (state.failure.kind === 'forbidden' || state.failure.kind === 'missing') {
      return <NotAvailablePage />;
    }
    return <FailurePanel failure={state.failure} onRetry={reload} />;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-md)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ maxWidth: 640 }}>
          <PageTitle>{copy.dataRoom.title}</PageTitle>
          <p
            style={{
              font: 'var(--type-body-md)',
              color: 'var(--text-secondary)',
              margin: 'calc(-1 * var(--space-md)) 0 var(--space-lg)',
            }}
          >
            {copy.dataRoom.intro}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link
            to="/data-room/new"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 44,
              padding: '0 20px',
              borderRadius: 'var(--radius-button)',
              background: 'var(--action-primary)',
              color: 'var(--text-on-primary)',
              font: 'var(--type-button-md)',
              textDecoration: 'none',
            }}
          >
            {copy.dataRoom.newDocument}
          </Link>
          {/* A download navigation, deliberately an anchor: the browser
              saves the attachment and the page stays put. */}
          <a
            href={DATA_ROOM_EXPORT_URL}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 44,
              padding: '0 20px',
              borderRadius: 'var(--radius-button)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-primary)',
              font: 'var(--type-button-md)',
              textDecoration: 'none',
              background: 'transparent',
            }}
          >
            {copy.dataRoom.download}
          </a>
          {workspaceSlots().dataRoomActions?.()}
        </div>
      </div>

      <div style={{ maxWidth: 420 }}>
        <TextField
          label={copy.dataRoom.searchLabel}
          value={filter}
          onChange={setFilter}
          placeholder={copy.dataRoom.searchPlaceholder}
        />
      </div>

      {matches !== null ? (
        matches.length === 0 ? (
          <p style={{ font: 'var(--type-body-md)', color: 'var(--text-muted)' }}>
            {copy.dataRoom.noMatches}
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {matches.map((document) => (
              <li
                key={document.id}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  font: 'var(--type-body-md)',
                }}
              >
                <Link
                  to={`/data-room/${document.id}`}
                  style={{ color: 'var(--text-link)' }}
                >
                  {document.title}
                </Link>
                <span style={{ color: 'var(--text-muted)' }}>
                  {copy.dataRoom.categories[document.category] ?? document.category}
                </span>
                {workspaceSlots().documentBadge?.({ audience: document.audience })}
                <EventTime iso={document.updatedAt} />
              </li>
            ))}
          </ul>
        )
      ) : (
        <>
          {/* The six permanent shelves. Always all six, empty or not. */}
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 var(--space-xl)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 'var(--space-md)',
            }}
          >
            {DATA_ROOM_CATEGORIES.map((category) => (
              <li key={category}>
                <Link
                  to={`/data-room/category/${category}`}
                  style={{
                    display: 'block',
                    height: '100%',
                    padding: 'var(--space-md)',
                    background: 'var(--surface-card)',
                    border: '1px solid var(--border-hairline)',
                    borderRadius: 'var(--radius-card)',
                    textDecoration: 'none',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      font: 'var(--type-heading-sm)',
                      letterSpacing: 'var(--type-heading-sm-tracking)',
                      color: 'var(--text-primary)',
                      marginBottom: 6,
                    }}
                  >
                    {copy.dataRoom.categories[category] ?? category}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      font: 'var(--type-caption)',
                      color: 'var(--text-muted)',
                      marginBottom: 10,
                    }}
                  >
                    {copy.dataRoom.categoryDefinitions[category]}
                  </span>
                  <span
                    style={{
                      font: 'var(--type-caption)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {copy.dataRoom.documentCount(countByCategory.get(category) ?? 0)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {workspaceSlots().dataRoomLinks?.()}

          {/* The archive door, shown only when there is an archive: a quiet
              link, because deleted work is the exception, not a shelf. */}
          {deletedCount > 0 ? (
            <p style={{ margin: '0 0 var(--space-lg)', font: 'var(--type-body-md)' }}>
              <Link to="/data-room/deleted" style={{ color: 'var(--text-link)' }}>
                {copy.dataRoom.deletedDocuments} ({deletedCount})
              </Link>
            </p>
          ) : null}

          {recent.length > 0 ? (
            <section>
              <SectionHeading>{copy.dataRoom.recent}</SectionHeading>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {recent.map((document) => (
                  <li
                    key={document.id}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'baseline',
                      flexWrap: 'wrap',
                      font: 'var(--type-body-md)',
                    }}
                  >
                    <Link
                      to={`/data-room/${document.id}`}
                      style={{ color: 'var(--text-link)' }}
                    >
                      {document.title}
                    </Link>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {copy.dataRoom.categories[document.category] ?? document.category}
                    </span>
                    {workspaceSlots().documentBadge?.({ audience: document.audience })}
                    <EventTime iso={document.updatedAt} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
    </>
  );
}
